import { z } from "zod";
import { CASE_RESULTS, CASE_STATUSES } from "../constants/index.js";

/**
 * 공통 프리미티브. 진실의 원천: harness/API-SPEC.md §0, apps/api/migrations/0001_init.sql
 */

// D1 INTEGER PRIMARY KEY AUTOINCREMENT
export const IdSchema = z.number().int().positive();

// harness/DATA-DICTIONARY.md §2 normalizeDate() 출력과 동일한 형식
/**
 * 업무 날짜.
 *
 * 모양만 보면 `2026-13-45` 가 통과한다. 그 값으로 조회하면 서버는 **0건**을 돌려주고,
 * 사람은 그것을 "그 기간에 아무 일도 없었다" 로 읽는다 — 오타 하나가 없는 사실을
 * 만들어 낸다. 그래서 달력에 실제로 있는 날인지까지 본다.
 */
export const IsoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "YYYY-MM-DD 형식이어야 한다")
  .refine((value) => {
    const year = Number(value.slice(0, 4));
    const month = Number(value.slice(5, 7));
    const day = Number(value.slice(8, 10));
    const date = new Date(Date.UTC(year, month - 1, day));
    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    );
  }, "달력에 없는 날짜다");
export const IsoDateTimeSchema = z.string().datetime({ offset: true }).or(
  z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/),
);

/**
 * 업무 데이터에는 날짜만 아는 값(입고일·출고일)과 시각까지 아는 값(증적 업로드)이
 * 함께 있다. 시각을 모를 때 00:00 을 지어내지 않기 위해 둘 다 허용한다 (SOUL S2).
 */
export const IsoDateOrDateTimeSchema = z.union([IsoDateSchema, IsoDateTimeSchema]);

// 목록 API 기본 페이지네이션 50건 (docs/01_아키텍처와_데이터모델.md §8, harness/UI-SPEC.md §6)
/**
 * 질의 문자열의 참/거짓.
 *
 * **`z.coerce.boolean()` 을 쓰지 않는다.** 그건 `Boolean(문자열)` 이라서 빈 문자열만
 * 거짓이다 — `"0"` 도 `"false"` 도 전부 **참**이 된다. 실제로 화면의
 * [엑셀 다운로드 (개인정보 제외)] 버튼이 `pii=false` 를 보내고 있었고, 서버는 그걸
 * "포함" 으로 읽어 담당자·연락처를 그대로 내려보냈다(반증 RT-21).
 *
 * 사람이 실제로 쓰는 표기(`1/0`, `true/false`, `yes/no`, 빈 값)를 그대로 받는다.
 * 모르는 값은 **참으로 넘기지 않는다** — 헷갈리면 안전한 쪽이 거짓이다.
 */
export const QueryBooleanSchema = z.preprocess((value) => {
  if (typeof value === "boolean") return value;
  if (value === undefined || value === null) return undefined;
  const text = String(value).trim().toLowerCase();
  if (text === "" || text === "0" || text === "false" || text === "no") return false;
  if (text === "1" || text === "true" || text === "yes" || text === "on") return true;
  return false;
}, z.boolean());

/**
 * 목록·내보내기의 회사 필터. **id 만 받는다.**
 *
 * 예전에는 `z.string()` 이라 `?company=나래넷츠` 도 200 이었고, 서버는 숫자가 아닌 값을
 * 조용히 버린 뒤 **전체**를 돌려줬다. 내보내기에서는 그게 곧 "한 업체 것" 이라고 믿고
 * 받은 파일에 **91건 전부**가 들어가는 일이 된다. 감사 로그에는 `company: "나래넷츠"` 가
 * 적혀 있어서 걸러진 것처럼 보이기까지 했다(반증 RT-25 · 같은 종류의 결함 C37).
 *
 * 다른 질의값(`status=NOPE`·`swap=maybe`·`limit=0`)은 전부 400 인데 이것만 통과했다.
 * 걸러 달라고 한 것을 못 거르겠으면 **막지 말고 되묻는다** — 조용히 전부 주지 않는다.
 */
export const CompanyIdQuerySchema = z.preprocess((value) => {
  if (value === undefined || value === null || value === "") return undefined;
  if (typeof value === "number") return value;
  const text = String(value).trim();
  return /^\d+$/.test(text) ? Number(text) : value; // 숫자가 아니면 그대로 흘려 실패시킨다
}, z.number().int().positive().optional());

export const PaginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.string().optional(),
});
export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;

export function paginatedResponse<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    next_cursor: z.string().nullable(),
    total: z.number().int().nonnegative().optional(),
  });
}

export const CaseStatusSchema = z.enum(CASE_STATUSES);
export const CaseResultSchema = z.enum(CASE_RESULTS).nullable();
